import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Stack } from '@mui/material';

import Typography from '../../../../components/Typography';
import { i18nNS } from '../../../../i18n';
import { TileDrawerButton } from '../../../shared/styles';
import PollList from './PollList';
import { Content, CreatePollButton, PollListContainer, pollsFormClasses } from './styles';
import usePollsForm, { SelectedPoll, UsePollsForm } from './vm';
import Accordion from './Accordion';
import AccordionGroup from './AccordionGroup';
import { PollTypes } from '../../../../db/polls/types';
import { format } from '../../../../utils/datetime';
import PollsSkeleton from './PollsSkeleton';

export type { SelectedPoll };

export interface PollsFormProps extends UsePollsForm {
  className?: string;
}

const PollsForm = ({ className, ...vmProps }: PollsFormProps) => {
  const { t } = useTranslation([i18nNS.POLL, i18nNS.GLOSSARY, i18nNS.SHARED]);

  const { selectedCourse, selectedClass } = vmProps;
  const {
    newPollUrl,
    isFetchingUnpublishedPolls,
    unpublishedPolls,
    formMethods,
    onSubmit,
    onClose,
    isFetchingPreviouslyUsedPolls,
    previouslyUsedPolls,
    selectedPollType,
  } = usePollsForm(vmProps);
  const { handleSubmit, control, formState } = formMethods;
  const { isValid, isSubmitting } = formState;

  return (
    <Stack gap={2} component="form" noValidate onSubmit={handleSubmit(onSubmit)} className={className}>
      <Content className={className}>
        <Typography variant="paragraphBold" component="h3">
          {t('add_a_poll', { ns: i18nNS.POLL })}
        </Typography>
        <Stack gap={2} px={2} flex={1} overflow="hidden">
          <CreatePollButton variant="outlined" size="small" fullWidth component={Link} to={newPollUrl ?? ''}>
            {t('create_a_new_poll', { ns: i18nNS.POLL })}
          </CreatePollButton>
          <PollListContainer className={pollsFormClasses.pollListContainer}>
            <AccordionGroup className={pollsFormClasses.pollAccordionGroup}>
              <Accordion
                identifier="unpublishedPolls"
                header={t('use_one_of_the_unpublished_polls', { ns: i18nNS.POLL })}
              >
                {isFetchingUnpublishedPolls ? (
                  <PollsSkeleton />
                ) : (
                  <Stack gap={2}>
                    <Typography
                      variant="paragraphBold"
                      color="grey.500"
                      px={2}
                      pb={1}
                      position="sticky"
                      top={0}
                      zIndex={1}
                      bgcolor="white"
                    >
                      {t('course_code_colon_course_title', {
                        ns: i18nNS.SHARED,
                        courseCode: selectedCourse.code,
                        courseTitle: selectedCourse.title,
                      })}
                    </Typography>
                    <PollList
                      control={control}
                      name="selectedPollId"
                      isLoading={false}
                      classStartTime={selectedClass.startTime}
                      polls={unpublishedPolls}
                      noDataMessage={t(
                        'there_are_no_unpublished_polls_attached_to_the_lecture_scheduled_at_scheduled_time',
                        {
                          ns: i18nNS.POLL,
                          dateTime: format(selectedClass.startTime, "hh:mm a 'on' do MMM, yyyy"),
                        }
                      )}
                      className={pollsFormClasses.pollList}
                    />
                  </Stack>
                )}
              </Accordion>
              <Accordion
                identifier="previouslyUsedPolls"
                header={t('use_a_previously_added_poll', { ns: i18nNS.POLL })}
              >
                {isFetchingPreviouslyUsedPolls ? (
                  <PollsSkeleton />
                ) : (
                  previouslyUsedPolls.map((courseData) => (
                    <Stack key={courseData.id} gap={2}>
                      <Typography
                        variant="paragraphBold"
                        color="grey.600"
                        px={2}
                        pb={1}
                        position="sticky"
                        top={0}
                        zIndex={1}
                        bgcolor="white"
                      >
                        {t('course_code_colon_course_title', {
                          ns: i18nNS.SHARED,
                          courseCode: courseData.code,
                          courseTitle: courseData.title,
                        })}
                      </Typography>
                      {courseData.activities.map((courseActivity) => (
                        <PollList
                          key={courseActivity.activityData.activityId}
                          control={control}
                          name="selectedPollId"
                          isLoading={false}
                          classStartTime={courseActivity.classData.startTime}
                          polls={[courseActivity.activityData]}
                          noDataMessage={t(
                            'no_polls_have_been_used_in_presentations_in_any_of_your_courses_yet',
                            { ns: i18nNS.POLL }
                          )}
                          className={pollsFormClasses.pollList}
                        />
                      ))}
                    </Stack>
                  ))
                )}
              </Accordion>
            </AccordionGroup>
          </PollListContainer>
        </Stack>
      </Content>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={2} component="footer">
        <TileDrawerButton type="button" variant="text" onClick={onClose}>
          {t('cancel', { ns: i18nNS.GLOSSARY })}
        </TileDrawerButton>
        <TileDrawerButton type="submit" isLoading={isSubmitting} disabled={!isValid || isSubmitting}>
          {selectedPollType && selectedPollType === PollTypes.PREVIOUSLY_USED
            ? t('copy', { ns: i18nNS.GLOSSARY })
            : t('next', { ns: i18nNS.GLOSSARY })}
        </TileDrawerButton>
      </Stack>
    </Stack>
  );
};

export default PollsForm;
